import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format, isBefore, startOfToday, addDays } from "date-fns";
import {
    Loader2,
    AlertTriangle,
    CheckCircle,
    Clock,
    X,
    ArrowDown,
    ArrowUp,
    Check,
    CheckCheck,
    Star,
    Trophy,
    Award,
    Zap,
    Target,
    Flag,
    Heart,
    ThumbsUp,
    Smile,
    PartyPopper,
    ArrowBigRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formConditionEvaluators } from "@/lib/formFieldConditions";
import {
    mergeFormTemplateWithSnapshot,
    normalizeFormFields,
    defaultValuesFromFields,
    FALLBACK_COMPLETION_FORM_FIELDS,
    resolutionIndicatesDeferred,
    getMissingRequiredFieldsWith,
    buildVisibleFormPayloadWith,
    validateDateFieldRange,
    getDateFieldRangeErrors,
} from "@/lib/workflowFormRuntime";
import { useToast } from "@/hooks/use-toast";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { router } from "@inertiajs/react";
import { Label } from "@/components/ui/label";
import ReactDOM from "react-dom";
import { DialogDescription } from "@/components/ui/dialog";
import Checklist from "@/components/ui/Checklist";
import { StackedAssigneeAvatars } from "@/components/tasks/stacked-assignee-avatars";

export function TaskDetailModal({
    open,
    onOpenChange,
    task,
    onComplete,
    onDefer,
    onAddNote,
    onUpdateStatus,
    isSubmitting = false,
    onViewParent = () => {},
    availableWorkflows,
    users,
    categories = [],
    csrf_token,
}) {
    const [activeTab, setActiveTab] = useState("details");
    const [newDueDate, setNewDueDate] = useState(undefined);
    const [noteContent, setNoteContent] = useState("");
    const [mentionSearch, setMentionSearch] = useState("");
    const [showMentions, setShowMentions] = useState(false);
    const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
    const [mentions, setMentions] = useState([]);
    const textareaRef = useRef(null);
    const mentionsContainerRef = useRef(null);
    const [today] = useState(startOfToday());
    const { toast } = useToast();
    const noteInputRef = useRef(null);
    const [notesSortDirection, setNotesSortDirection] = useState("desc");
    const [isDeferPopoverOpen, setIsDeferPopoverOpen] = useState(false);

    // Track if this is the initial open
    const [isInitialOpen, setIsInitialOpen] = useState(true);

    // State for post-completion form - MUST be before early return
    const [showPostCompletionForm, setShowPostCompletionForm] = useState(false);
    const [postCompletionFormData, setPostCompletionFormData] = useState({});

    // State to force re-renders when checklist changes
    const [checklistVersion, setChecklistVersion] = useState(0);

    // State for tooltip
    const [showTooltip, setShowTooltip] = useState(false);

    // State for note saving loading
    const [isSavingNote, setIsSavingNote] = useState(false); // Added for note saving loading
    const lastAppliedChecklistRef = useRef(null);
    const noteSaveCompletedRef = useRef(false); // Track if a note save just completed
    const [isNoteInputFocused, setIsNoteInputFocused] = useState(false); // Track if note input is focused
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // Track if there are unsaved changes
    const [isChecklistEditing, setIsChecklistEditing] = useState(false); // Track if checklist is being edited
    const [originalValues, setOriginalValues] = useState({}); // Track original values for error handling
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [isUpdatingPriority, setIsUpdatingPriority] = useState(false);
    const [isUpdatingCategory, setIsUpdatingCategory] = useState(false);
    const [isUpdatingDeferrable, setIsUpdatingDeferrable] = useState(false);
    const [isAcknowledging, setIsAcknowledging] = useState(false);
    const hasAutoAcknowledgedRef = useRef(false);
    
    // Local task state to track updates (especially for acknowledgment)
    const [localTask, setLocalTask] = useState(task);
    
    // Update local task when prop changes
    useEffect(() => {
        if (task) {
            setLocalTask(task);
        }
    }, [task]);

    useEffect(() => {
        if (open && task) {
            if (isInitialOpen) {
                setActiveTab("details");
                setIsInitialOpen(false);
            }
            setNewDueDate(undefined);
            setNoteContent("");
            setMentions([]);
            setShowMentions(false);

            // Store original values for error handling
            setOriginalValues({
                status: task.status,
                priority: task.priority,
                category: task.category,
                deferrable: task.deferrable,
            });

            // Initialize the last applied checklist ref
            if (task.checklist) {
                lastAppliedChecklistRef.current = JSON.stringify(
                    task.checklist
                );
            }
        } else if (!open) {
            setIsInitialOpen(true);
        }
    }, [open, task, isInitialOpen]);
    
    // Update local task when prop changes
    useEffect(() => {
        if (task) {
            setLocalTask(task);
        }
    }, [task]);

    // Add a new effect to close mentions dropdown when tab changes
    useEffect(() => {
        setShowMentions(false);
    }, [activeTab]);

    // Add a click outside handler for the mentions dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                mentionsContainerRef.current &&
                !mentionsContainerRef.current.contains(event.target) &&
                textareaRef.current &&
                !textareaRef.current.contains(event.target)
            ) {
                setShowMentions(false);
            }
        };

        if (showMentions) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showMentions]);

    // Add this after the other useEffect hooks
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape" && showMentions) {
                setShowMentions(false);
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [showMentions]);

    // Auto-acknowledge when modal opens if task is unacknowledged
    // This must be before the early return to maintain hook order
    useEffect(() => {
        if (open && localTask && !localTask.is_acknowledged && !hasAutoAcknowledgedRef.current) {
            hasAutoAcknowledgedRef.current = true;
            // Use router directly here to avoid dependency on handleAcknowledge
            const taskId = localTask.id;
            router.post(`/tasks/${taskId}/acknowledge`, {}, {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => {
                    // Update local task state immediately for UI responsiveness
                    setLocalTask(prev => ({ ...prev, is_acknowledged: true, acknowledged_at: new Date().toISOString() }));
                },
                onError: () => {
                    // Silently fail for auto-acknowledge - reset ref so it can retry
                    hasAutoAcknowledgedRef.current = false;
                }
            });
        } else if (!open) {
            hasAutoAcknowledgedRef.current = false;
        }
    }, [open, localTask]);

    // Use localTask for acknowledgment checks to get immediate UI updates
    // Use useMemo to ensure reactivity when localTask updates
    const displayTask = useMemo(() => localTask || task, [localTask, task]);
    if (!displayTask) return null;

    const getStatusColor = (status) => {
        switch (status) {
            case "Completed":
                return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
            case "In Progress":
                return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
            case "To Do":
                return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400";
            case "In Review":
                return "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400";
            default:
                return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400";
        }
    };

    const getPriorityColor = (priority) => {
        const priorityNum = Number.parseInt(priority, 10);
        if (priorityNum >= 8) {
            return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
        } else if (priorityNum >= 5) {
            return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400";
        } else {
            return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
        }
    };

    // Get completion settings from task settings
    const completionSettings = task.settings || {};
    const completionButtonText =
        completionSettings.completion_button_text || "Complete Task";
    const completionButtonColor =
        completionSettings.completion_button_color || "#10b981";
    const completionButtonIcon =
        completionSettings.completion_button_icon || "CheckCircle";
    const requirePostCompletionForm =
        completionSettings.require_post_completion_form || false;
    const postCompletionFormId =
        completionSettings.post_completion_form_id || null;
    const captureDeferDateInPostCompletionForm = Boolean(
        completionSettings.capture_defer_date_in_post_completion_form,
    );
    const postCompletionDeferDateFieldId =
        completionSettings.post_completion_defer_date_field_id || null;
    const deferUiHandledByPostCompletionForm =
        (task.deferrable === 1 || task.deferrable === true) &&
        requirePostCompletionForm &&
        !!postCompletionFormId &&
        captureDeferDateInPostCompletionForm &&
        !!String(postCompletionDeferDateFieldId || "").trim();

    // Icon mapping for completion button
    const iconMap = {
        CheckCircle,
        Check,
        CheckCheck,
        Star,
        Trophy,
        Award,
        Zap,
        Target,
        Flag,
        Heart,
        ThumbsUp,
        Smile,
        PartyPopper,
        ArrowBigRight,
    };

    const CompletionIcon = iconMap[completionButtonIcon] || CheckCircle;

    const handleComplete = async () => {
        try {
            // Check if this is a parent task with incomplete subtasks
            if (task.isParent && task.subtasks && task.subtasks.length > 0) {
                const incompleteSubtasks = task.subtasks.filter(subtask => subtask.status !== "Completed");
                if (incompleteSubtasks.length > 0) {
                    toast({
                        title: "Cannot Complete Parent Task",
                        description: `This task has ${incompleteSubtasks.length} incomplete subtask(s). Please complete all subtasks before marking this task as complete.`,
                        variant: "destructive",
                    });
                    return;
                }
            }

            // Check if all checklist items are completed
            // if (task.checklist && task.checklist.length > 0) {
            //   const incompleteItems = task.checklist.filter(item => !item.checked)
            //   if (incompleteItems.length > 0) {
            //     toast({
            //       title: "Cannot Complete Task",
            //       description: "You must complete all checklist items before this task can be marked complete.",
            //       variant: "destructive",
            //     })
            //     return
            //   }
            // }

            // If post-completion form is required, show the form first
            if (requirePostCompletionForm) {
                setShowPostCompletionForm(true);
                return;
            }

            // Otherwise, complete the task directly
            await completeTask();
        } catch (error) {
            // If there's an error, show an error toast
            toast({
                title: "Error",
                description: "Failed to complete task. Please try again.",
                variant: "destructive",
            });
        }
    };

    const completeTask = async (formData = null) => {
        try {
            // Call the complete function and wait for it to finish
            await onComplete(task.id, formData);

            // Close the modal and form immediately after successful completion
            setShowPostCompletionForm(false);
            onOpenChange(false);

            // Success toast moved to parent component
        } catch (error) {
            // If there's an error, show an error toast
            toast({
                title: "Error",
                description: "Failed to complete task. Please try again.",
                variant: "destructive",
            });
        }
    };

    const handlePostCompletionFormSubmit = async (formData) => {
        // Note: Checklist validation has been removed - tasks can now be completed regardless of checklist status
        await completeTask(formData);
    };

    /** Defer via post-completion form (same as Defer Task button) — does not complete the task. */
    const handleDeferFromPostCompletionForm = (dateStr) => {
        onDefer(task.id, dateStr);
        setShowPostCompletionForm(false);
        onOpenChange(false);
    };

    const handleDefer = () => {
        if (newDueDate) {
            // Extract date components directly to avoid timezone issues
            const year = newDueDate.getFullYear();
            const month = String(newDueDate.getMonth() + 1).padStart(2, "0");
            const day = String(newDueDate.getDate()).padStart(2, "0");
            const dateString = `${year}-${month}-${day}`;
            onDefer(task.id, dateString);
            setIsDeferPopoverOpen(false);
        }
    };

    const handleAcknowledge = async (isAuto = false) => {
        if (!task || task.is_acknowledged) return;
        
        setIsAcknowledging(true);
        try {
            await router.post(`/tasks/${task.id}/acknowledge`, {}, {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => {
                    // Update task locally
                    if (onUpdateStatus) {
                        // Update the task's acknowledgment status
                        const updatedTask = { ...task, is_acknowledged: true, acknowledged_at: new Date().toISOString() };
                        onUpdateStatus(task.id, task.status, updatedTask);
                    }
                    if (!isAuto) {
                        toast({
                            title: "Success",
                            description: "Task acknowledged successfully.",
                        });
                    }
                },
                onError: (errors) => {
                    toast({
                        title: "Error",
                        description: "Failed to acknowledge task. Please try again.",
                        variant: "destructive",
                    });
                },
                onFinish: () => {
                    setIsAcknowledging(false);
                }
            });
        } catch (error) {
            setIsAcknowledging(false);
            toast({
                title: "Error",
                description: "Failed to acknowledge task. Please try again.",
                variant: "destructive",
            });
        }
    };

    // Add a helper function to get the effective due date
    const getEffectiveDueDate = (task) => {
        return task.defer_date || task.due || task.dueDate;
    };

    // Parse due-like date strings as calendar dates first (ignore timezone/time
    // when a YYYY-MM-DD prefix exists) to avoid previous-day display shifts.
    const parseCalendarDate = (value) => {
        if (!value) return null;
        const raw = String(value).trim();
        const datePrefixMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (datePrefixMatch) {
            const year = Number(datePrefixMatch[1]);
            const month = Number(datePrefixMatch[2]);
            const day = Number(datePrefixMatch[3]);
            const date = new Date(year, month - 1, day);
            return Number.isNaN(date.getTime()) ? null : date;
        }
        const fallback = new Date(raw);
        return Number.isNaN(fallback.getTime()) ? null : fallback;
    };

    // Calculate if task is overdue
    const isOverdue = (() => {
        const effectiveDate = getEffectiveDueDate(task);
        if (
            !effectiveDate ||
            effectiveDate === "" ||
            task.status === "Completed"
        )
            return false;

        // Parse the date properly to avoid timezone issues
        const taskDate = parseCalendarDate(effectiveDate);
        if (!taskDate) return false;

        // A task is overdue if it's due before today (not including today)
        const todayStart = new Date(today);
        todayStart.setHours(0, 0, 0, 0);

        return taskDate < todayStart;
    })();

    // Helper function to get caret coordinates in a textarea
    const getCaretCoordinates = (element, position) => {
        const { offsetLeft, offsetTop } = element;
        const div = document.createElement("div");
        const styles = getComputedStyle(element);

        div.style.position = "absolute";
        div.style.top = "0";
        div.style.left = "0";
        div.style.visibility = "hidden";
        div.style.whiteSpace = "pre-wrap";
        div.style.width = styles.width;
        div.style.fontSize = styles.fontSize;
        div.style.fontFamily = styles.fontFamily;
        div.style.lineHeight = styles.lineHeight;
        div.style.padding = styles.padding;

        div.textContent = element.value.substring(0, position);

        const span = document.createElement("span");
        span.textContent = element.value.substring(position) || ".";
        div.appendChild(span);

        document.body.appendChild(div);
        const coordinates = {
            top: offsetTop + span.offsetTop,
            left: offsetLeft + span.offsetLeft,
        };
        document.body.removeChild(div);

        return coordinates;
    };

    const handleNoteInput = (e) => {
        setNoteContent(e.currentTarget.innerHTML);
        setHasUnsavedChanges(e.currentTarget.innerHTML.trim().length > 0);

        // Get caret position and text before caret
        const sel = window.getSelection();
        if (!sel || !sel.anchorNode) {
            setShowMentions(false);
            return;
        }
        // Get the text content up to the caret
        let caretPos = sel.anchorOffset;
        let node = sel.anchorNode;
        let textBeforeCaret = "";
        if (node.nodeType === Node.TEXT_NODE) {
            textBeforeCaret = node.textContent.slice(0, caretPos);
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            // If caret is in an element node, try to get the text up to the caret
            let range = sel.getRangeAt(0).cloneRange();
            range.selectNodeContents(noteInputRef.current);
            range.setEnd(sel.anchorNode, sel.anchorOffset);
            textBeforeCaret = range.toString();
        }
        // Find the last @ before the caret
        const atSymbolIndex = textBeforeCaret.lastIndexOf("@");
        let searchText = "";
        if (
            atSymbolIndex !== -1 &&
            (atSymbolIndex === 0 ||
                textBeforeCaret[atSymbolIndex - 1] === " ") &&
            caretPos > atSymbolIndex
        ) {
            searchText = textBeforeCaret.substring(atSymbolIndex + 1);
            setMentionSearch(searchText);
            // Position the mention dropdown
            if (noteInputRef.current) {
                // Get bounding rect for caret
                const range = sel.getRangeAt(0).cloneRange();
                range.collapse(true);
                const rect = range.getBoundingClientRect();
                setMentionPosition({
                    top: rect.bottom + window.scrollY,
                    left: rect.left + window.scrollX,
                });
            }
            setShowMentions(true);
        } else {
            setShowMentions(false);
        }
    };

    const insertMention = (user) => {
        if (!noteInputRef.current) return;
        const sel = window.getSelection();
        if (!sel.rangeCount) return;
        const range = sel.getRangeAt(0);

        // Find the last @ in the text before the caret
        let node = sel.anchorNode;
        let caretPos = sel.anchorOffset;
        let textBeforeCaret = "";
        if (node.nodeType === Node.TEXT_NODE) {
            textBeforeCaret = node.textContent.slice(0, caretPos);
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            let tempRange = range.cloneRange();
            tempRange.selectNodeContents(noteInputRef.current);
            tempRange.setEnd(sel.anchorNode, sel.anchorOffset);
            textBeforeCaret = tempRange.toString();
        }
        const atSymbolIndex = textBeforeCaret.lastIndexOf("@");
        if (atSymbolIndex !== -1) {
            // Move range to start of @
            if (node.nodeType === Node.TEXT_NODE) {
                range.setStart(node, atSymbolIndex);
            } else {
                // For element nodes, walk to the text node and set start
                let tempNode = node;
                let offset = atSymbolIndex;
                while (tempNode && tempNode.nodeType !== Node.TEXT_NODE) {
                    tempNode = tempNode.firstChild;
                }
                if (tempNode) {
                    range.setStart(tempNode, offset);
                }
            }
            range.deleteContents();
        }

        // Create the mention span
        const mentionSpan = document.createElement("span");
        mentionSpan.contentEditable = "false";
        mentionSpan.className = "mention-pill";
        mentionSpan.dataset.userid = user.id;
        mentionSpan.textContent = `@${user.name}`;
        // Insert the mention
        range.insertNode(mentionSpan);
        // Add a space after
        range.collapse(false);
        const space = document.createTextNode(" ");
        range.insertNode(space);
        // Move caret after the space
        range.setStartAfter(space);
        range.setEndAfter(space);
        sel.removeAllRanges();
        sel.addRange(range);
        setShowMentions(false);
        setMentions((prev) => [...prev, user.id]);
        setNoteContent(noteInputRef.current.innerHTML);
    };

    const filteredUsers = users
        .filter((user) => {
            const searchLower = mentionSearch.toLowerCase();
            const nameLower = user.name?.toLowerCase() || "";
            const emailLower = user.email?.toLowerCase() || "";
            return (
                nameLower.includes(searchLower) ||
                emailLower.includes(searchLower)
            );
        })
        .slice(0, 5);

    // Format note content to highlight @mentions
    const formatNoteContent = (content) => {
        let formatted = content;
        users.forEach((user) => {
            // Replace @Name with a mention-pill span (only if not already inside a span)
            const regex = new RegExp(`(?<![>\w])@${user.name}(?![\w<])`, "g");
            formatted = formatted.replace(
                regex,
                `<span class=\"mention-pill\">@${user.name}</span>`
            );
        });
        return formatted;
    };

    const handleAddNote = async () => {
        if (noteContent.trim()) {
            setIsSavingNote(true);
            try {
                await onAddNote(task.id, noteContent, mentions);
                setNoteContent("");
                if (noteInputRef.current) noteInputRef.current.innerHTML = "";
                setMentions([]);
                setHasUnsavedChanges(false);
            } catch (error) {
                console.error("📝 Failed to add note:", error);
            } finally {
                setIsSavingNote(false);
                noteSaveCompletedRef.current = true; // Mark that a note save just completed
                setTimeout(() => {
                    noteSaveCompletedRef.current = false; // Reset after a short delay
                }, 1000); // Give enough time for any re-renders
            }
        }
    };

    // Add this function inside the TaskDetailModal component, before the return statement
    const handleOpenTaskDetail = (subtask) => {
        if (onViewParent && subtask.id) {
            // We're reusing the onViewParent function to open any task
            onViewParent(subtask.id);
        }
    };

    const handleWorkflowClick = () => {
        if (task.workflow_id) {
            router.visit(`/workflows/${task.workflow_id}`);
        }
    };

    const handleStatusChange = async (newStatus) => {
        try {
            setIsUpdatingStatus(true);
            // Update local task state immediately for instant feedback
            task.status = newStatus;

            // Call the API
            await onUpdateStatus(task.id, newStatus);

            // Success toast handled by parent component (Dashboard)
        } catch (error) {
            // Revert on error
            task.status = originalValues.status;
            toast({
                title: "Error",
                description: "Failed to update task status. Please try again.",
                variant: "destructive",
            });
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    // Handle priority change
    const handlePriorityChange = async (newPriority) => {
        try {
            setIsUpdatingPriority(true);
            // Update local task state immediately for instant feedback
            task.priority = newPriority;

            const response = await fetch('/tasks', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrf_token,
                },
                body: JSON.stringify({
                    id: task.id,
                    priority: newPriority,
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to update priority');
            }

            toast({
                title: 'Priority Updated',
                description: `Task priority updated to "${newPriority}"`,
                variant: 'success',
            });
        } catch (error) {
            // Revert on error
            task.priority = originalValues.priority;
            toast({
                title: 'Error',
                description: 'Failed to update task priority. Please try again.',
                variant: 'destructive',
            });
        } finally {
            setIsUpdatingPriority(false);
        }
    };

    // Handle category change
    const handleCategoryChange = async (newCategory) => {
        try {
            setIsUpdatingCategory(true);
            // Update local task state immediately for instant feedback
            task.category = newCategory;

            const response = await fetch('/tasks', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrf_token,
                },
                body: JSON.stringify({
                    id: task.id,
                    category: newCategory,
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to update category');
            }

            toast({
                title: 'Category Updated',
                description: `Task category updated to "${newCategory}"`,
                variant: 'success',
            });
        } catch (error) {
            // Revert on error
            task.category = originalValues.category;
            toast({
                title: 'Error',
                description: 'Failed to update task category. Please try again.',
                variant: 'destructive',
            });
        } finally {
            setIsUpdatingCategory(false);
        }
    };

    // Handle deferrable change
    const handleDeferrableChange = async (newDeferrable) => {
        try {
            setIsUpdatingDeferrable(true);
            // Update local task state immediately for instant feedback
            task.deferrable = newDeferrable;

            const response = await fetch('/tasks', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrf_token,
                },
                body: JSON.stringify({
                    id: task.id,
                    deferrable: newDeferrable,
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to update deferrable setting');
            }

            toast({
                title: 'Deferrable Setting Updated',
                description: `Task is now ${newDeferrable ? 'deferrable' : 'not deferrable'}`,
                variant: 'success',
            });
        } catch (error) {
            // Revert on error
            task.deferrable = originalValues.deferrable;
            toast({
                title: 'Error',
                description: 'Failed to update deferrable setting. Please try again.',
                variant: 'destructive',
            });
        } finally {
            setIsUpdatingDeferrable(false);
        }
    };

    // Add this helper function to properly format dates
    const formatTaskDate = (dateString) => {
        if (!dateString) return "";

        try {
            const date = parseCalendarDate(dateString);
            if (!date) {
                console.warn("Invalid date string:", dateString);
                return dateString;
            }

            return date.toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
            });
        } catch (error) {
            console.error("Error formatting date:", { dateString, error });
            return dateString;
        }
    };

    // Add this helper function to format description text to preserve line breaks
    const formatDescription = (description) => {
        if (!description) return "";
        
        // Replace \n with <br> tags and handle both \n and </br> patterns
        let formatted = description
            .replace(/\\n/g, '<br><br>')
            .replace(/<\/br>/g, '<br><br>')
            .replace(/\n/g, '<br><br>');
        
        // Add word-break opportunities in URLs to allow them to wrap
        // This adds <wbr> tags after common URL characters to enable wrapping
        // Handle both "https://" and "https: //" formats
        formatted = formatted.replace(/(https?\s*:\s*\/\/[^\s<]+)/g, (url) => {
            return url.replace(/([\/\.\?\&\=\%\-\_\:])/g, '$1<wbr>');
        });
        return formatted;
    };

    // Inject mention-pill CSS globally so it applies to both input and display
    if (
        typeof window !== "undefined" &&
        !document.getElementById("mention-pill-style")
    ) {
        const style = document.createElement("style");
        style.id = "mention-pill-style";
        style.innerHTML = `
      .mention-pill {
        display: inline-block;
        background: linear-gradient(90deg, #3b82f6 0%, #6366f1 100%);
        color: #fff;
        font-weight: bold;
        border-radius: 9999px;
        padding: 0 0.5em;
        margin: 0 0.1em;
        box-shadow: 0 1px 4px rgba(59,130,246,0.12);
        font-size: 0.95em;
        cursor: pointer;
      }
      .mention-pill:after {
        content: '\u200B';
      }
      
      /* Tooltip positioning for assignee avatars */
      .tooltip-left {
        left: 0;
        transform: none;
      }
      
      .tooltip-left .absolute {
        left: 1rem;
        transform: none;
      }
      
      .tooltip-center {
        left: 50%;
        transform: translateX(-50%);
      }
      
      .tooltip-center .absolute {
        left: 50%;
        transform: translateX(-50%);
      }
    `;
        document.head.appendChild(style);
    }

    // Add state to track if the note input is empty
    const isNoteInputEmpty =
        !noteContent ||
        noteContent === "<br>" ||
        noteContent.replace(/<[^>]+>/g, "").trim() === "";

    // Check if checklist is incomplete
    const hasIncompleteChecklist =
        task.checklist &&
        task.checklist.length > 0 &&
        task.checklist.some((item) => !item.checked);

  // Check if parent task has incomplete subtasks
  const hasIncompleteSubtasks = 
    task.isParent && 
    task.subtasks && 
    task.subtasks.length > 0 && 
    task.subtasks.some((subtask) => subtask.status !== "Completed");

  // Check if task can be completed
  const canCompleteTask = !hasIncompleteSubtasks;

    // Sort notes by created_at
    const sortedNotes = (task.notes || []).slice().sort((a, b) => {
        if (notesSortDirection === "asc") {
            return new Date(a.created_at) - new Date(b.created_at);
        } else {
            return new Date(b.created_at) - new Date(a.created_at);
        }
    });

    // Custom handler for modal open/close
    const handleOpenChange = (newOpen) => {
        if (!newOpen && hasUnsavedChanges) {
            // Show a toast warning only for unsaved notes
            toast({
                title: "Unsaved Changes",
                description: "Please save or cancel your changes before closing the modal.",
                variant: "destructive",
            });
            return;
        }
        // For checklist editing, just prevent closing without showing toast
        if (!newOpen && isChecklistEditing) {
            return;
        }
        onOpenChange(newOpen);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="w-full max-w-none sm:max-w-[600px] lg:max-w-[800px] h-full sm:h-[600px] lg:h-5/6 lg:max-h-[800px] flex flex-col overflow-hidden">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {displayTask.name}
                        {!displayTask.is_acknowledged && (
                            <Badge
                                variant="outline"
                                className="bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400"
                            >
                                New
                            </Badge>
                        )}
                        {displayTask.is_acknowledged && (
                            <Badge
                                variant="outline"
                                className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                            >
                                Acknowledged
                            </Badge>
                        )}
                        {task.isParent && task.subtasks && task.subtasks.length > 0 && (
                            <Badge
                                variant="outline"
                                className="bg-primary/10 text-primary"
                            >
                                Parent
                            </Badge>
                        )}
                        {task.is_shared && (
                            <Badge
                                variant="outline"
                                className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
                            >
                                Shared
                            </Badge>
                        )}
                        {isOverdue && (
                            <Badge
                                variant="outline"
                                className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                            >
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                Overdue
                            </Badge>
                        )}
                    </DialogTitle>
                    <DialogDescription className="sr-only">
                        Task details and management interface
                    </DialogDescription>
                </DialogHeader>

                <Tabs
                    defaultValue="details"
                    value={activeTab}
                    onValueChange={setActiveTab}
                    className="flex-1 flex flex-col overflow-hidden"
                >
                    <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="details">Details</TabsTrigger>
                        <TabsTrigger
                            value="checklist"
                            className="flex items-center"
                        >
                            Checklist
                            {task.checklist?.length > 0 && (
                                <Badge
                                    variant="secondary"
                                    className="ml-1.5 h-5 px-1.5 flex items-center justify-center"
                                >
                                    {
                                        task.checklist.filter(
                                            (item) => item.checked
                                        ).length
                                    }
                                    /{task.checklist.length}
                                </Badge>
                            )}
                        </TabsTrigger>
                        <TabsTrigger
                            value="notes"
                            className="flex items-center"
                        >
                            Notes
                            {task.notes?.length > 0 && (
                                <Badge
                                    variant="secondary"
                                    className="ml-1.5 h-5 px-1.5 flex items-center justify-center"
                                >
                                    {task.notes.length}
                                </Badge>
                            )}
                        </TabsTrigger>
                    </TabsList>

                    <style dangerouslySetInnerHTML={{
                        __html: `
                            [role="tabpanel"][data-state="inactive"] {
                                display: none !important;
                            }
                        `
                    }} />

                    <TabsContent
                        value="details"
                        className="flex-1 overflow-hidden mt-4 px-1"
                    >
                        <ScrollArea className="pr-4 h-full overflow-y-auto">
                            <div className="space-y-6 pr-4 pb-8">
                                <div>
                                    <h3 className="text-sm font-medium text-muted-foreground mb-1">
                                        Description
                                    </h3>
                                    <div 
                                        className="text-sm whitespace-pre-wrap break-words"
                                        dangerouslySetInnerHTML={{
                                            __html: formatDescription(task.description)
                                        }}
                                    />
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <h3 className="text-sm font-medium text-muted-foreground mb-1">
                                            Status
                                        </h3>
                                        <div className="flex items-center gap-2">
                                            <Select
                                                value={task.status}
                                                onValueChange={handleStatusChange}
                                                disabled={isSubmitting || isUpdatingStatus}
                                                open={isUpdatingStatus ? true : undefined}
                                            >
                                                <SelectTrigger className="w-[150px] focus:ring-0 focus-visible:ring-0 focus:ring-offset-0">
                                                    <SelectValue>
                                                        {isUpdatingStatus ? (
                                                            <div className="flex items-center gap-2">
                                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                                <span>Updating...</span>
                                                            </div>
                                                        ) : (
                                                            task.status
                                                        )}
                                                    </SelectValue>
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {task.status === "Completed" ? (
                                                        <>
                                                            <SelectItem value="In Progress" disabled={isUpdatingStatus}>
                                                                Revert to In Progress
                                                            </SelectItem>
                                                            <SelectItem value="To Do" disabled={isUpdatingStatus}>
                                                                Revert to To Do
                                                            </SelectItem>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <SelectItem value="To Do" disabled={isUpdatingStatus}>
                                                                To Do
                                                            </SelectItem>
                                                            <SelectItem value="In Progress" disabled={isUpdatingStatus}>
                                                                In Progress
                                                            </SelectItem>
                                                        </>
                                                    )}
                                                </SelectContent>
                                            </Select>
                                            {task.status === "Completed" && (task.completedBy || task.completed_by) && (
                                                <p className="text-xs text-muted-foreground mt-1.5">
                                                    Completed by {task.completedBy?.name ?? (task.completed_by ? `User #${task.completed_by}` : '')}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-medium text-muted-foreground mb-1">
                                            Priority
                                        </h3>
                                        {task.status !== "Completed" ? (
                                            <Select
                                                value={task.priority}
                                                onValueChange={handlePriorityChange}
                                                disabled={isSubmitting || isUpdatingPriority}
                                                open={isUpdatingPriority ? true : undefined}
                                            >
                                                <SelectTrigger className="w-[150px] focus:ring-0 focus-visible:ring-0 focus:ring-offset-0 border-none pl-0 [&>svg]:hidden">
                                                    <SelectValue>
                                                        {isUpdatingPriority ? (
                                                            <div className="flex items-center gap-2">
                                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                                <span>Updating...</span>
                                                            </div>
                                                        ) : (
                                                            <Badge
                                                                variant="outline"
                                                                className={cn(
                                                                    "w-full justify-start",
                                                                    getPriorityColor(task.priority)
                                                                )}
                                                            >
                                                                {task.priority}
                                                            </Badge>
                                                        )}
                                                    </SelectValue>
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="1" disabled={isUpdatingPriority}>1 (Lowest)</SelectItem>
                                                    <SelectItem value="2" disabled={isUpdatingPriority}>2</SelectItem>
                                                    <SelectItem value="3" disabled={isUpdatingPriority}>3</SelectItem>
                                                    <SelectItem value="4" disabled={isUpdatingPriority}>4</SelectItem>
                                                    <SelectItem value="5" disabled={isUpdatingPriority}>5</SelectItem>
                                                    <SelectItem value="6" disabled={isUpdatingPriority}>6</SelectItem>
                                                    <SelectItem value="7" disabled={isUpdatingPriority}>7</SelectItem>
                                                    <SelectItem value="8" disabled={isUpdatingPriority}>8</SelectItem>
                                                    <SelectItem value="9" disabled={isUpdatingPriority}>9</SelectItem>
                                                    <SelectItem value="10" disabled={isUpdatingPriority}>10 (Highest)</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        ) : (
                                            <Badge
                                                variant="outline"
                                                className={getPriorityColor(
                                                    task.priority
                                                )}
                                            >
                                                {task.priority}
                                            </Badge>
                                        )}
                                    </div>

                                    <div>
                                        <h3 className="text-sm font-medium text-muted-foreground mb-1">
                                            Category
                                        </h3>
                                        {task.status !== "Completed" ? (
                                            <Select
                                                value={task.category}
                                                onValueChange={handleCategoryChange}
                                                disabled={isSubmitting || isUpdatingCategory}
                                                open={isUpdatingCategory ? true : undefined}
                                            >
                                                <SelectTrigger className="w-[150px] focus:ring-0 focus-visible:ring-0 focus:ring-offset-0">
                                                    <SelectValue>
                                                        {isUpdatingCategory ? (
                                                            <div className="flex items-center gap-2">
                                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                                <span>Updating...</span>
                                                            </div>
                                                        ) : (
                                                            task.category
                                                        )}
                                                    </SelectValue>
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {categories.map((category) => (
                                                        <SelectItem key={category.id} value={category.name} disabled={isUpdatingCategory}>
                                                            {category.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        ) : (
                                            <span className="text-sm">
                                                {task.category}
                                            </span>
                                        )}
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-medium text-muted-foreground mb-1">
                                            Deferrable
                                        </h3>
                                        {task.status !== "Completed" ? (
                                            <Select
                                                value={task.deferrable ? "1" : "0"}
                                                onValueChange={(value) => handleDeferrableChange(value === "1")}
                                                disabled={isSubmitting || isUpdatingDeferrable}
                                                open={isUpdatingDeferrable ? true : undefined}
                                            >
                                                <SelectTrigger className="w-[150px] focus:ring-0 focus-visible:ring-0 focus:ring-offset-0">
                                                    <SelectValue>
                                                        {isUpdatingDeferrable ? (
                                                            <div className="flex items-center gap-2">
                                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                                <span>Updating...</span>
                                                            </div>
                                                        ) : (
                                                            task.deferrable ? "Yes" : "No"
                                                        )}
                                                    </SelectValue>
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="1" disabled={isUpdatingDeferrable}>Yes</SelectItem>
                                                    <SelectItem value="0" disabled={isUpdatingDeferrable}>No</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        ) : (
                                            <span className="text-sm">
                                                {task.deferrable ? "Yes" : "No"}
                                            </span>
                                        )}
                                    </div>

                                    <div>
                                        <h3 className="text-sm font-medium text-muted-foreground mb-1">
                                            Assignee
                                        </h3>
                                        <StackedAssigneeAvatars
                                            users={task.users}
                                            size="lg"
                                            showNameForSingle={false}
                                        />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-medium text-muted-foreground mb-1">
                                            Due Date
                                        </h3>
                                        <div className="text-sm">
                                            {formatTaskDate(
                                                getEffectiveDueDate(task)
                                            )}
                                            {task.defer_date && (
                                                <span className="ml-2 text-muted-foreground">
                                                    (Deferred)
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Task Type Categories - only show if categories exist */}
                                    {task.taskTypeCategories &&
                                        task.taskTypeCategories.length > 0 && (
                                            <div>
                                                <h3 className="text-sm font-medium text-muted-foreground mb-1">
                                                    Task Type Categories
                                                </h3>
                                                <div className="flex flex-wrap gap-2">
                                                    {task.taskTypeCategories.map(
                                                        (cat) => {
                                                            // Match the color and label from task-form-modal
                                                            const options = {
                                                                quality: {
                                                                    label: "Quality",
                                                                    color: "#10b981",
                                                                },
                                                                speed: {
                                                                    label: "Speed",
                                                                    color: "#3b82f6",
                                                                },
                                                                procedural: {
                                                                    label: "Procedural",
                                                                    color: "#8b5cf6",
                                                                },
                                                            };
                                                            const opt = options[
                                                                cat
                                                            ] || {
                                                                label: cat,
                                                                color: "#6b7280",
                                                            };
                                                            return (
                                                                <span
                                                                    key={cat}
                                                                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white rounded-full"
                                                                    style={{
                                                                        backgroundColor:
                                                                            opt.color,
                                                                    }}
                                                                >
                                                                    {opt.label}
                                                                </span>
                                                            );
                                                        }
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                    {/* Link */}
                                    <div>
                                        <h3 className="text-sm font-medium text-muted-foreground mb-1">
                                            Link
                                        </h3>
                                        {task.link ? (
                                            <a
                                                href={task.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-sm text-primary hover:underline break-all"
                                            >
                                                {task.link_title
                                                    ? task.link_title
                                                    : task.link}
                                            </a>
                                        ) : (
                                            <span className="text-muted-foreground text-sm italic">
                                                None
                                            </span>
                                        )}
                                    </div>

                                    {/* Probability of Closing Sale Slider for Sales Tasks */}
                                    {task.taskType === "sales" &&
                                        task.salesProbability > 0 && (
                                            <div className="space-y-4 border rounded-lg p-4 bg-muted/10">
                                                <h3 className="text-sm font-medium text-muted-foreground mb-1">
                                                    Probability of Closing Sale
                                                </h3>
                                                <ProbabilitySlider />
                                            </div>
                                        )}

                                    <div>
                                        <h3 className="text-sm font-medium text-muted-foreground mb-1">
                                            ID
                                        </h3>
                                        <div className="text-sm font-mono">
                                            {task.id}
                                        </div>
                                    </div>
                                </div>

                                {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                </div> */}

                                {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                </div> */}

                                {!task.isParent && task.parent_id && (
                                    <div className="p-3 border rounded-lg bg-muted/10 mb-4">
                                        <h3 className="text-sm font-medium mb-2">
                                            Parent Task
                                        </h3>
                                        <div className="flex items-center gap-2">
                                            <Badge
                                                variant="outline"
                                                className="bg-primary/10 text-primary"
                                            >
                                                Parent
                                            </Badge>
                                            <button
                                                className="text-sm text-primary hover:underline font-medium"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleOpenChange(false);
                                                    // Small delay to avoid UI flicker
                                                    setTimeout(() => {
                                                                                                            if (task.parent_id) {
                                                        onViewParent(
                                                            task.parent_id
                                                        );
                                                    }
                                                    }, 100);
                                                }}
                                            >
                                                {task.parentName ||
                                                    task.parent_id}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {task.subtasks && task.subtasks.length > 0 && (
                                    <div className="border rounded-lg p-3 mt-4 mb-6 flex flex-col">
                                        <div className="flex items-center justify-between mb-2">
                                            <h3 className="text-sm font-medium">
                                                Subtasks ({task.subtasks.length})
                                            </h3>
                                            {/* Progress Bar */}
                                            <div className="flex items-center gap-2">
                                                <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                                                    <div 
                                                        className="h-full bg-green-500 transition-all duration-300"
                                                        style={{
                                                            width: `${(task.subtasks.filter(subtask => subtask.status === "Completed").length / task.subtasks.length) * 100}%`
                                                        }}
                                                    />
                                                </div>
                                                <span className="text-xs text-muted-foreground">
                                                    {task.subtasks.filter(subtask => subtask.status === "Completed").length}/{task.subtasks.length}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="space-y-2 max-h-[250px] overflow-y-auto pr-2">
                                            {task.subtasks.map((subtask) => (
                                                <div
                                                    key={subtask.id}
                                                    className="flex items-center justify-between p-2 rounded border hover:bg-muted/20 cursor-pointer"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenChange(false);
                                                        // Small delay to avoid UI flicker
                                                        setTimeout(() => {
                                                            handleOpenTaskDetail(
                                                                subtask
                                                            );
                                                        }, 100);
                                                    }}
                                                >
                                                    <div className="flex items-center gap-2">
                                                                                                            <div
                                                        className={cn(
                                                            "h-2 w-2 rounded-full",
                                                            subtask.status ===
                                                                "Completed"
                                                                ? "bg-green-500"
                                                                : "bg-red-500"
                                                        )}
                                                    />
                                                        <span className="text-sm font-medium">
                                                            {subtask.name}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Badge
                                                            variant="outline"
                                                            className={cn(
                                                                getStatusColor(
                                                                    subtask.status
                                                                ),
                                                                "text-xs"
                                                            )}
                                                        >
                                                            {subtask.status}
                                                        </Badge>
                                                        <Badge
                                                            variant="outline"
                                                            className={cn(
                                                                getPriorityColor(
                                                                    subtask.priority
                                                                ),
                                                                "text-xs"
                                                            )}
                                                        >
                                                            {subtask.priority}
                                                        </Badge>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </ScrollArea>
                    </TabsContent>

                    <TabsContent
                        value="checklist"
                        className="flex-1 flex flex-col overflow-hidden mt-4"
                    >
                        <ScrollArea className="overflow-y-auto overflow-x-hidden">
                            <div className="p-4">
                                <Checklist
                                    key={`checklist-${checklistVersion}`}
                                    items={task.checklist || []}
                                    onUpdate={async (updatedChecklist) => {
                                        try {
                                            // Optimistically update the UI first
                                            const originalChecklist = [
                                                ...(task.checklist || []),
                                            ];
                                            Object.assign(task, {
                                                checklist: updatedChecklist,
                                            });
                                            setChecklistVersion(
                                                (prev) => prev + 1
                                            ); // Force re-render

                                            // Ensure all checklist items have required fields
                                            const sanitizedChecklist = updatedChecklist.map(item => ({
                                                label: item.label || '',
                                                checked: item.checked || false,
                                                createdBy: item.createdBy || window.currentUser?.id?.toString() || '1',
                                                createdAt: item.createdAt || new Date().toISOString(),
                                                ...(item.checked && (!item.checkedBy || !item.checkedAt) ? {
                                                    checkedBy: window.currentUser?.id?.toString() || '1',
                                                    checkedAt: new Date().toISOString()
                                                } : {}),
                                                ...item // Preserve any other fields
                                            }))

                                            const response = await fetch(
                                                `/tasks/${task.id}/checklist`,
                                                {
                                                    method: "PUT",
                                                    headers: {
                                                        "Content-Type":
                                                            "application/json",
                                                        "X-CSRF-TOKEN": csrf_token,
                                                    },
                                                    body: JSON.stringify({
                                                        checklist: sanitizedChecklist,
                                                    }),
                                                }
                                            );

                                            if (!response.ok) {
                                                throw new Error(
                                                    "Failed to update checklist"
                                                );
                                            }

                                            // Update with the actual server response if needed
                                            const result =
                                                await response.json();

                                            if (result.success && result.task) {
                                                // Only update if the server response differs from our optimistic update
                                                const serverChecklist =
                                                    result.task.checklist || [];
                                                const optimisticChecklist =
                                                    updatedChecklist;

                                                const needsUpdate =
                                                    serverChecklist.length !==
                                                        optimisticChecklist.length ||
                                                    serverChecklist.some(
                                                        (item, index) =>
                                                            !optimisticChecklist[
                                                                index
                                                            ] ||
                                                            item.checked !==
                                                                optimisticChecklist[
                                                                    index
                                                                ].checked
                                                    );

                                                if (needsUpdate) {
                                                    Object.assign(
                                                        task,
                                                        result.task
                                                    );
                                                    setChecklistVersion(
                                                        (prev) => prev + 1
                                                    ); // Force re-render
                                                } else {
                                                    // console.log(
                                                    //     "✅ Server response matches optimistic update - no UI update needed"
                                                    // );
                                                }
                                            }
                                        } catch (error) {
                                            console.error(
                                                "❌ Error updating checklist:",
                                                {
                                                    timestamp:
                                                        new Date().toISOString(),
                                                    error: error.message,
                                                    stack: error.stack,
                                                }
                                            );

                                            // Note: We don't revert here since the user already made the change
                                            // and we want to preserve their intent
                                            toast({
                                                title: "Error",
                                                description:
                                                    "Failed to update checklist",
                                                variant: "destructive",
                                            });
                                        }
                                    }}
                                    onToggleItem={async (index) => {
                                        try {
                                            // Optimistically update the UI first
                                            const optimisticChecklist = [
                                                ...(task.checklist || []),
                                            ];
                                            const originalChecked =
                                                optimisticChecklist[index]
                                                    ?.checked;
                                            const newChecked = !originalChecked;

                                            optimisticChecklist[index] = {
                                                ...optimisticChecklist[index],
                                                checked: newChecked,
                                            };

                                            Object.assign(task, {
                                                checklist: optimisticChecklist,
                                            });
                                            setChecklistVersion(
                                                (prev) => prev + 1
                                            ); // Force re-render

                                            // Update the ref to track this optimistic change
                                            lastAppliedChecklistRef.current =
                                                JSON.stringify(
                                                    optimisticChecklist
                                                );

                                            const response = await fetch(
                                                `/tasks/${task.id}/checklist/${index}`,
                                                {
                                                    method: "PATCH",
                                                    headers: {
                                                        "Content-Type":
                                                            "application/json",
                                                        "X-CSRF-TOKEN": csrf_token,
                                                    },
                                                }
                                            );

                                            if (!response.ok) {
                                                throw new Error(
                                                    "Failed to toggle checklist item"
                                                );
                                            }

                                            const result =
                                                await response.json();

                                            if (result.success && result.task) {
                                                // Only update if the server response differs significantly from our optimistic update
                                                const serverChecklist =
                                                    result.task.checklist || [];
                                                const optimisticChecklist =
                                                    task.checklist || [];

                                                const serverChecked =
                                                    serverChecklist[index]
                                                        ?.checked;
                                                const optimisticChecked =
                                                    optimisticChecklist[index]
                                                        ?.checked;

                                                // Check if the server response matches what we expect
                                                const expectedChecked =
                                                    !originalChecked; // This is what we optimistically set

                                                // Only update if server response doesn't match our expectation
                                                if (
                                                    serverChecked !==
                                                    expectedChecked
                                                ) {
                                                    Object.assign(
                                                        task,
                                                        result.task
                                                    );
                                                    setChecklistVersion(
                                                        (prev) => prev + 1
                                                    ); // Force re-render
                                                    lastAppliedChecklistRef.current =
                                                        JSON.stringify(
                                                            result.task
                                                                .checklist
                                                        );
                                                } else {
                                                    console.log(
                                                        "✅ Server toggle response matches our expectation - no UI update needed"
                                                    );
                                                }
                                            }
                                        } catch (error) {
                                            console.error(
                                                "❌ Error toggling checklist item:",
                                                {
                                                    timestamp:
                                                        new Date().toISOString(),
                                                    index,
                                                    error: error.message,
                                                    stack: error.stack,
                                                }
                                            );

                                            // Revert optimistic update on error
                                            const revertedChecklist = [
                                                ...(task.checklist || []),
                                            ];
                                            revertedChecklist[index] = {
                                                ...revertedChecklist[index],
                                                checked:
                                                    !revertedChecklist[index]
                                                        .checked,
                                            };
                                            Object.assign(task, {
                                                checklist: revertedChecklist,
                                            });
                                            setChecklistVersion(
                                                (prev) => prev + 1
                                            ); // Force re-render
                                            lastAppliedChecklistRef.current =
                                                JSON.stringify(
                                                    revertedChecklist
                                                );

                                            toast({
                                                title: "Error",
                                                description:
                                                    "Failed to toggle checklist item",
                                                variant: "destructive",
                                            });
                                        }
                                    }}
                                    onInteraction={(isEditing) => {
                                        setIsChecklistEditing(isEditing);
                                    }}
                                    isCollapsible={false}
                                    showProgress={true}
                                    isCheckable={true}
                                />
                            </div>
                        </ScrollArea>
                    </TabsContent>

                    <TabsContent
                        value="notes"
                        className="flex-1 flex flex-col overflow-hidden mt-4"
                    >
                        <ScrollArea className="overflow-y-auto overflow-x-hidden">
                            <div className="flex items-center justify-end my-2 px-1">
                                <button
                                    type="button"
                                    className="flex items-center gap-1 px-2 py-1 rounded hover:bg-muted transition-colors border border-transparent hover:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                                    title={`Sort by date (${
                                        notesSortDirection === "asc"
                                            ? "Oldest first"
                                            : "Newest first"
                                    })`}
                                    onClick={() =>
                                        setNotesSortDirection((dir) =>
                                            dir === "asc" ? "desc" : "asc"
                                        )
                                    }
                                >
                                    {notesSortDirection === "asc" ? (
                                        <ArrowUp className="w-4 h-4" />
                                    ) : (
                                        <ArrowDown className="w-4 h-4" />
                                    )}
                                    <span className="text-xs text-muted-foreground">
                                        {notesSortDirection === "asc"
                                            ? "Oldest"
                                            : "Newest"}
                                    </span>
                                </button>
                            </div>
                            {sortedNotes.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full text-center pb-4">
                                    <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center mb-2">
                                        <span className="text-muted-foreground text-lg">
                                            📝
                                        </span>
                                    </div>
                                    <h3 className="text-lg font-medium">
                                        No notes yet
                                    </h3>
                                    <p className="text-sm text-muted-foreground">
                                        Add the first note to this task
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-4 pb-8">
                                    {sortedNotes.map((note) => (
                                        <div
                                            key={note.id}
                                            className="p-3 border rounded-lg"
                                        >
                                            <div className="flex items-center gap-2 mb-2">
                                                <Avatar className="h-6 w-6">
                                                    <AvatarImage
                                                        src={
                                                            note.author
                                                                ?.avatar ||
                                                            "/placeholder.svg"
                                                        }
                                                        alt={note.author?.name}
                                                        className="object-cover"
                                                    />
                                                    <AvatarFallback className="bg-primary text-white font-bold flex items-center justify-center text-xs">
                                                        {note.author?.name
                                                            ? note.author.name
                                                                  .split(" ")
                                                                  .map(
                                                                      (n) =>
                                                                          n[0]
                                                                  )
                                                                  .join("")
                                                            : "?"}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <span className="font-medium text-sm">
                                                    {note.author?.name}
                                                </span>
                                                <span className="text-xs text-muted-foreground">
                                                    {format(
                                                        new Date(
                                                            note.created_at
                                                        ),
                                                        "MMM d, yyyy 'at' h:mm a"
                                                    )}
                                                </span>
                                            </div>
                                            <div
                                                className="text-sm"
                                                dangerouslySetInnerHTML={{
                                                    __html: formatNoteContent(
                                                        note.note
                                                    ),
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </ScrollArea>

                        <div className="border-t pt-4 mt-4">
                            <div className="relative px-1">
                                <div
                                    ref={noteInputRef}
                                    contentEditable={!isSubmitting}
                                    className="min-h-[100px] border rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-primary/50 bg-white dark:bg-gray-900 text-sm"
                                    onInput={handleNoteInput}
                                    onFocus={() => {
                                        setIsNoteInputFocused(true);
                                        setHasUnsavedChanges(noteContent.trim().length > 0);
                                    }}
                                    onBlur={() => {
                                        setIsNoteInputFocused(false);
                                        // Don't clear hasUnsavedChanges here as we want to keep it until save/cancel
                                    }}
                                    style={{
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                    }}
                                    suppressContentEditableWarning
                                    aria-label="Add a note"
                                />
                                {isNoteInputEmpty && (
                                    <span
                                        className="absolute left-3 top-2 text-muted-foreground pointer-events-none select-none text-sm"
                                        style={{ zIndex: 1 }}
                                    >
                                        Add a note... Use @ to mention someone
                                    </span>
                                )}
                                {showMentions &&
                                    filteredUsers.length > 0 &&
                                    ReactDOM.createPortal(
                                        <div
                                            ref={mentionsContainerRef}
                                            className="fixed bg-background border rounded-md shadow-lg w-[250px] max-h-[200px] overflow-y-auto overflow-x-hidden pointer-events-auto z-[99999]"
                                            tabIndex={0}
                                            style={{
                                                top: mentionPosition.top,
                                                left: mentionPosition.left,
                                            }}
                                            onWheel={(e) => e.stopPropagation()} // Prevent background/modal from scrolling
                                        >
                                            {filteredUsers.map((user, idx) => (
                                                <div
                                                    key={user.id}
                                                    className="flex items-center gap-2 p-2 hover:bg-muted cursor-pointer"
                                                    tabIndex={0}
                                                    onClick={() =>
                                                        insertMention(user)
                                                    }
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter")
                                                            insertMention(user);
                                                    }}
                                                >
                                                    <Avatar className="h-6 w-6">
                                                        <AvatarImage
                                                            src={user.avatar}
                                                            alt={user.name}
                                                            className="object-cover"
                                                        />
                                                        <AvatarFallback className="bg-primary text-white font-bold flex items-center justify-center text-xs">
                                                            {user.name
                                                                ? user.name
                                                                      .split(
                                                                          " "
                                                                      )
                                                                      .map(
                                                                          (n) =>
                                                                              n[0]
                                                                      )
                                                                      .join("")
                                                                : user.firstName &&
                                                                  user.lastName
                                                                ? `${user.firstName[0]}${user.lastName[0]}`
                                                                : user.email
                                                                ? user.email[0].toUpperCase()
                                                                : "U"}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <div>
                                                        <div className="text-sm font-medium">
                                                            {user.name}
                                                        </div>
                                                        <div className="text-xs text-muted-foreground">
                                                            {user.email}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>,
                                        document.body
                                    )}
                            </div>

                            <div className="flex justify-start mt-2 gap-2">
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setNoteContent("");
                                        if (noteInputRef.current) {
                                            noteInputRef.current.innerHTML = "";
                                        }
                                        setMentions([]);
                                        setHasUnsavedChanges(false);
                                    }}
                                    disabled={isSavingNote}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleAddNote}
                                    disabled={
                                        !noteContent.trim() || isSavingNote
                                    }
                                >
                                    {(() => {
                                        return isSavingNote ? (
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        ) : null;
                                    })()}
                                    Save
                                </Button>
                            </div>
                        </div>
                    </TabsContent>
                </Tabs>

                <DialogFooter className="flex flex-col gap-2 border-t pt-4 mt-auto bg-background">
                    {(hasUnsavedChanges || isChecklistEditing) && (
                        <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-200">
                            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                            <span className="text-sm">
                                {hasUnsavedChanges && isChecklistEditing 
                                    ? "You have unsaved changes in both notes and checklist. Please save or cancel your changes before closing."
                                    : hasUnsavedChanges 
                                    ? "You have unsaved changes in notes. Please save or cancel your changes before closing."
                                    : "You are editing the checklist. Please finish editing before closing."
                                }
                            </span>
                        </div>
                    )}
                    <div
                        className={`flex-col sm:flex-row flex gap-2 w-full sm:w-full sm:ml-auto items-end ${
                            (task.deferrable === 1 || task.deferrable === true) &&
                            task.status !== "Completed" &&
                            !deferUiHandledByPostCompletionForm
                                ? "justify-between"
                                : "justify-end"
                        }`}
                    >
                        {hasIncompleteSubtasks && (
                            <div className="text-xs text-muted-foreground mt-2 text-center">
                                ⚠️ Complete all subtasks before marking this task as complete
                            </div>
                        )}
                        {(task.deferrable === 1 || task.deferrable === true) &&
                            task.status !== "Completed" &&
                            !deferUiHandledByPostCompletionForm && (
                                <Popover
                                    open={isDeferPopoverOpen}
                                    onOpenChange={setIsDeferPopoverOpen}
                                >
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className="w-full sm:w-auto bg-primary text-white hover:bg-primary/70 hover:text-white"
                                            disabled={hasUnsavedChanges || isChecklistEditing}
                                        >
                                            <Clock className="mr-2 h-4 w-4" />
                                            Defer Task
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                        className="w-auto p-0"
                                        align="start"
                                    >
                                        <div className="p-2 text-center">
                                            <h3 className="font-medium mb-2">
                                                Select new due date
                                            </h3>
                                            <Calendar
                                                mode="single"
                                                selected={newDueDate}
                                                onSelect={setNewDueDate}
                                                disabled={(date) => {
                                                    // Disable past dates and today
                                                    const today = new Date();
                                                    today.setHours(0, 0, 0, 0);
                                                    return date < today;
                                                }}
                                                initialFocus
                                            />
                                            <div className="flex justify-end mt-4">
                                                <Button
                                                    size="sm"
                                                    onClick={handleDefer}
                                                    disabled={!newDueDate}
                                                >
                                                    Confirm
                                                </Button>
                                            </div>
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            )}
                        <div className="flex gap-2 w-full sm:w-auto">
                            <Button
                                variant="outline"
                                onClick={() => handleOpenChange(false)}
                                className="w-full sm:w-auto"
                                disabled={hasUnsavedChanges || isChecklistEditing}
                            >
                                <X className="mr-2 h-4 w-4" />
                                Close
                            </Button>
                            {task.status !== "Completed" ? (
                                <Button 
                                    onClick={handleComplete} 
                                    className="w-full sm:w-auto dark:text-white" 
                                    disabled={!canCompleteTask || isSubmitting || isNoteInputFocused || hasUnsavedChanges || isChecklistEditing}
                                    style={{
                                        backgroundColor: canCompleteTask ? completionButtonColor : '#9ca3af',
                                        borderColor: canCompleteTask ? completionButtonColor : '#9ca3af',
                                    }}
                                >
                                    {(() => {
                                        // Don't show loading spinner if we're saving a note or if we just finished saving a note
                                        // The isSubmitting might stay true after note saving, so we need to be more careful
                                        const shouldShowLoading = isSubmitting && !isSavingNote && !noteContent.trim();                                    
                                        // Additional safety check: if we're in any note-related state, don't show loading
                                        const isInNoteState = isSavingNote || noteContent.trim().length > 0;
                                        const finalShouldShowLoading = shouldShowLoading && !isInNoteState;

                                        // Additional check: if a note save just completed, don't show loading
                                        const shouldShowLoadingFinal = finalShouldShowLoading && !noteSaveCompletedRef.current;

                                        return shouldShowLoadingFinal ? (
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        ) : (
                                            <CompletionIcon className="mr-2 h-4 w-4" />
                                        );
                                    })()}
                                    {completionButtonText}
                                </Button>
                            ) : (
                                <Button 
                                    className="w-full sm:w-auto bg-green-600 text-white" 
                                    disabled
                                >
                                    <CheckCircle className="mr-2 h-4 w-4" />
                                    Task Completed
                                </Button>
                            )}
                        </div>
                    </div>
                </DialogFooter>
            </DialogContent>

            {/* Post-Completion Form Modal */}
            {showPostCompletionForm && (
                <Dialog
                    open={showPostCompletionForm}
                    onOpenChange={setShowPostCompletionForm}
                >
                    <DialogContent className="w-5/6 sm:w-10/12 lg:w-auto max-w-none sm:max-w-[600px] lg:max-w-[800px] h-auto min-h-0 max-h-[90vh] flex flex-col overflow-hidden">
                        <DialogHeader className="pr-4">
                            <DialogTitle>
                                {deferUiHandledByPostCompletionForm
                                    ? `Task: ${task.name}`
                                    : `Complete Task: ${task.name}`}
                            </DialogTitle>
                            <DialogDescription>
                                {deferUiHandledByPostCompletionForm
                                    ? "Complete the task or defer it by choosing Deferred and a defer date."
                                    : "Please fill out the required information before completing this task."}
                            </DialogDescription>
                        </DialogHeader>

                        <PostCompletionForm
                            formId={postCompletionFormId}
                            taskId={task.id}
                            onSubmit={handlePostCompletionFormSubmit}
                            onCancel={() => setShowPostCompletionForm(false)}
                            isSubmitting={isSubmitting}
                            deferCaptureEnabled={deferUiHandledByPostCompletionForm}
                            postCompletionDeferDateFieldId={
                                deferUiHandledByPostCompletionForm
                                    ? String(postCompletionDeferDateFieldId || "").trim()
                                    : ""
                            }
                            onDeferFromPostForm={handleDeferFromPostCompletionForm}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </Dialog>
    );
}

// Add this helper component inside the file, above the TaskDetailModal export
function ProbabilitySlider() {
    const [value, setValue] = useState(50);
    const marks = [
        { label: "Very Low", value: 10 },
        { label: "Low", value: 30 },
        { label: "Medium", value: 50 },
        { label: "High", value: 75 },
        { label: "Very High", value: 90 },
    ];
    return (
        <div className="w-full flex flex-col items-center gap-2">
            <div className="w-full flex items-center justify-between text-xs text-muted-foreground mb-1">
                <span>Low (0%)</span>
                <span>High (100%)</span>
            </div>
            <div className="w-full flex flex-col items-center">
                <input
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={value}
                    onChange={(e) => setValue(Number(e.target.value))}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                    style={{
                        background:
                            "linear-gradient(90deg, #ef4444 0%, #f59e42 50%, #22c55e 100%)",
                    }}
                />
                <div className="text-lg font-bold mt-2">{value}%</div>
            </div>
            <div className="w-full flex justify-between mt-2 gap-1">
                {marks.map((opt) => (
                    <button
                        key={opt.value}
                        type="button"
                        className={`px-2 py-1 rounded border text-xs flex-1 transition-colors ${
                            value === opt.value
                                ? "border-yellow-400 bg-yellow-50 text-yellow-700 font-bold"
                                : "border-gray-200 bg-white hover:bg-gray-50"
                        }`}
                        onClick={() => setValue(opt.value)}
                    >
                        {opt.label}
                        <br />
                        <span className="font-bold">{opt.value}%</span>
                    </button>
                ))}
            </div>
        </div>
    );
}

/** Native date input: open picker from any click on the control (not only the calendar icon). */
function PostCompletionDateField({ id, value, onChange, required, className, min, max }) {
    const inputRef = useRef(null);

    const openPicker = useCallback(() => {
        const el = inputRef.current;
        if (!el) return;
        try {
            if (typeof el.showPicker === "function") {
                el.showPicker();
                return;
            }
        } catch {
            // showPicker can throw (e.g. not visible) — fall through
        }
        el.focus();
        el.click();
    }, []);

    return (
        <div
            className="relative w-full cursor-pointer [&_input]:cursor-pointer"
            onMouseDownCapture={(e) => {
                if (e.button !== 0) return;
                e.preventDefault();
                openPicker();
            }}
            onKeyDownCapture={(e) => {
                if (e.key !== "Enter" && e.key !== " ") return;
                e.preventDefault();
                openPicker();
            }}
        >
            <input
                ref={inputRef}
                type="date"
                id={id}
                value={value}
                onChange={onChange}
                required={required}
                min={min || undefined}
                max={max || undefined}
                className={cn(className, "cursor-pointer")}
            />
        </div>
    );
}

// Post-Completion Form Component
function PostCompletionForm({
    formId,
    taskId,
    onSubmit,
    onCancel,
    isSubmitting,
    deferCaptureEnabled = false,
    postCompletionDeferDateFieldId = "",
    onDeferFromPostForm,
}) {
    const [formData, setFormData] = useState({});
    const [formFields, setFormFields] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [formName, setFormName] = useState("");
    const [formSettings, setFormSettings] = useState({});
    const [numberFieldTotal, setNumberFieldTotal] = useState(0);
    const [fieldErrors, setFieldErrors] = useState({});
    const { toast } = useToast();

    const deferDateFieldId = String(postCompletionDeferDateFieldId || "").trim();

    const { isFieldVisible: baseFieldVisible, isFieldRequired: baseFieldRequired } =
        useMemo(
            () => formConditionEvaluators(formData, formFields),
            [formData, formFields],
        );

    const isDeferDateField = useCallback(
        (field) =>
            deferCaptureEnabled &&
            !!deferDateFieldId &&
            field?.id != null &&
            String(field.id) === deferDateFieldId,
        [deferCaptureEnabled, deferDateFieldId],
    );

    const isFieldVisible = useCallback(
        (field) => {
            if (isDeferDateField(field)) {
                return resolutionIndicatesDeferred(
                    formData,
                    formFields,
                    deferDateFieldId,
                );
            }
            return baseFieldVisible(field);
        },
        [
            isDeferDateField,
            formData,
            formFields,
            deferDateFieldId,
            baseFieldVisible,
        ],
    );

    const isFieldRequired = useCallback(
        (field) => {
            if (!isFieldVisible(field)) return false;
            if (isDeferDateField(field)) return true;
            return baseFieldRequired(field);
        },
        [isFieldVisible, isDeferDateField, baseFieldRequired],
    );

    // Validate number field value against min/max constraints
    const validateNumberField = useCallback((fieldId, value) => {
        const field = formFields.find(f => f.id === fieldId);
        if (!field || field.type !== 'number') return null;
        if (!isFieldVisible(field)) return null; // uses wrapped visibility (defer date, conditionals)

        const numValue = parseFloat(value);
        if (isNaN(numValue)) return null;

        const errors = [];
        
        if (field.validation?.min !== undefined && numValue < field.validation.min) {
            errors.push(`Value must be at least ${field.validation.min}`);
        }
        
        if (field.validation?.max !== undefined && numValue > field.validation.max) {
            errors.push(`Value must be no more than ${field.validation.max}`);
        }

        return errors.length > 0 ? errors.join(', ') : null;
    }, [formFields, isFieldVisible]);

    const validateDateField = useCallback((fieldId, value) => {
        const field = formFields.find((f) => f.id === fieldId);
        if (!field || field.type !== "date") return null;
        if (!isFieldVisible(field)) return null;
        return validateDateFieldRange(value, field.validation);
    }, [formFields, isFieldVisible]);

    // Calculate total of number fields
    const calculateNumberFieldTotal = useCallback(() => {
        if (!formSettings.calculateNumberFields) {
            setNumberFieldTotal(0);
            return;
        }

        const numberFields = formFields.filter(
            (field) =>
                field.type === "number" && isFieldVisible(field),
        );
        const total = numberFields.reduce((sum, field) => {
            const value = formData[field.id];
            const numValue = parseFloat(value) || 0;
            return sum + numValue;
        }, 0);
        
        setNumberFieldTotal(total);
    }, [formData, formFields, formSettings.calculateNumberFields, isFieldVisible]);

    // Calculate maximum possible total
    const calculateMaxTotal = useCallback(() => {
        const numberFields = formFields.filter(
            (field) =>
                field.type === "number" && isFieldVisible(field),
        );
        return numberFields.reduce((sum, field) => {
            const maxValue = field.validation?.max;
            return sum + (maxValue !== undefined ? maxValue : 0);
        }, 0);
    }, [formFields, isFieldVisible]);

    // Update total whenever form data changes
    useEffect(() => {
        calculateNumberFieldTotal();
    }, [calculateNumberFieldTotal]);

    useEffect(() => {
        // Load form fields if formId and taskId are provided
        if (formId && taskId) {
            loadFormInstance();
        } else if (formId) {
            loadFormFields();
        } else {
            setFormFields(FALLBACK_COMPLETION_FORM_FIELDS);
            setIsLoading(false);
        }
    }, [formId, taskId]);

    const loadFormInstance = async () => {
        try {
            // Load the FormInstance data for this task and form
            const response = await fetch(
                `/tasks/${taskId}/form-instance/${formId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error(
                    `Failed to load form instance: ${response.status}`
                );
            }

            const formInstance = await response.json();

            // Set form name and settings from the form instance (use form_data which has variables substituted)
            setFormName(formInstance.form_data?.name || formInstance.form?.name || "Dynamic Form");
            setFormSettings(formInstance.form_data?.settings || {});
            const transformedFields = mergeFormTemplateWithSnapshot(formInstance);

            setFormFields(transformedFields);
            setFormData(defaultValuesFromFields(transformedFields));
        } catch (error) {
            console.error("Failed to load form instance:", error);
            // Fallback to loading the original form
            await loadFormFields();
        } finally {
            setIsLoading(false);
        }
    };

    const loadFormFields = async () => {
        try {
            if (!formId) {
                // Use default form fields if no formId provided
                setFormFields([
                    {
                        id: "completion_notes",
                        label: "Completion Notes",
                        type: "textarea",
                        required: true,
                    },
                    {
                        id: "completion_date",
                        label: "Completion Date",
                        type: "date",
                        required: true,
                    },
                    {
                        id: "completion_method",
                        label: "Completion Method",
                        type: "select",
                        required: true,
                        options: [
                            { value: "manual", label: "Manual" },
                            { value: "automated", label: "Automated" },
                            { value: "external", label: "External System" },
                        ],
                    },
                ]);
                setIsLoading(false);
                return;
            }

            // Load form data from the API
            const response = await fetch(`/forms/${formId}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to load form: ${response.status}`);
            }

            const formData = await response.json();

            // Set form name and settings
            setFormName(formData.name || "Dynamic Form");
            setFormSettings(formData.settings || {});

            const transformedFields = normalizeFormFields(formData.fields || []);

            setFormFields(transformedFields);
            setFormData(defaultValuesFromFields(transformedFields));
        } catch (error) {
            console.error("Failed to load form fields:", error);
            setFormFields(FALLBACK_COMPLETION_FORM_FIELDS);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFieldChange = (fieldId, value) => {
        setFormData((prev) => ({
            ...prev,
            [fieldId]: value,
        }));

        // Validate number / date fields
        const field = formFields.find(f => f.id === fieldId);
        if (field && field.type === 'number') {
            const error = validateNumberField(fieldId, value);
            setFieldErrors(prev => ({
                ...prev,
                [fieldId]: error
            }));
        } else if (field && field.type === 'date') {
            const error = validateDateField(fieldId, value);
            setFieldErrors(prev => ({
                ...prev,
                [fieldId]: error
            }));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const missingFields = getMissingRequiredFieldsWith(
            formFields,
            formData,
            isFieldVisible,
            isFieldRequired,
        );

        if (missingFields.length > 0) {
            toast({
                title: "Missing required fields",
                description: missingFields.map((f) => f.label).join(", "),
                variant: "destructive",
            });
            return;
        }

        const hasValidationErrors = formFields.some(
            (f) =>
                isFieldVisible(f) &&
                fieldErrors[f.id] != null &&
                fieldErrors[f.id] !== "",
        );
        const dateRangeErrors = getDateFieldRangeErrors(
            formFields,
            formData,
            isFieldVisible,
        );
        const hasDateRangeErrors = Object.keys(dateRangeErrors).length > 0;
        if (hasDateRangeErrors) {
            setFieldErrors((prev) => ({ ...prev, ...dateRangeErrors }));
        }
        if (hasValidationErrors || hasDateRangeErrors) {
            toast({
                title: "Validation error",
                description: "Please fix the highlighted fields.",
                variant: "destructive",
            });
            return;
        }

        const willDefer =
            deferCaptureEnabled &&
            !!deferDateFieldId &&
            typeof onDeferFromPostForm === "function" &&
            resolutionIndicatesDeferred(
                formData,
                formFields,
                deferDateFieldId,
            );

        if (willDefer) {
            const raw = formData[deferDateFieldId];
            const ymd = raw != null ? String(raw).trim().slice(0, 10) : "";
            if (!ymd) {
                toast({
                    title: "Defer date required",
                    description: "Choose a defer date to defer this task.",
                    variant: "destructive",
                });
                return;
            }
            onDeferFromPostForm(ymd);
            return;
        }

        onSubmit(
            buildVisibleFormPayloadWith(formFields, formData, isFieldVisible),
        );
    };

    const renderField = (field, effectiveRequired) => {
        switch (field.type) {
            case "textarea":
                return (
                    <Textarea
                        id={field.id}
                        value={formData[field.id] || ""}
                        onChange={(e) =>
                            handleFieldChange(field.id, e.target.value)
                        }
                        placeholder={
                            field.placeholder ||
                            `Enter ${field.label.toLowerCase()}...`
                        }
                        required={effectiveRequired}
                        className="min-h-[100px]"
                    />
                );

            case "date":
                return (
                    <div className="space-y-1">
                        <PostCompletionDateField
                            id={field.id}
                            value={formData[field.id] || ""}
                            onChange={(e) =>
                                handleFieldChange(field.id, e.target.value)
                            }
                            required={effectiveRequired}
                            min={field.validation?.minDate || undefined}
                            max={field.validation?.maxDate || undefined}
                            className="relative flex h-10 w-full cursor-pointer rounded-md border border-input bg-background py-2 pl-3 pr-10 text-sm text-foreground ring-offset-background [color-scheme:light] placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:[color-scheme:dark] [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-2 [&::-webkit-calendar-picker-indicator]:top-1/2 [&::-webkit-calendar-picker-indicator]:-translate-y-1/2 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-datetime-edit-fields-wrapper]:cursor-pointer [&::-moz-calendar-picker-indicator]:absolute [&::-moz-calendar-picker-indicator]:right-2 [&::-moz-calendar-picker-indicator]:top-1/2 [&::-moz-calendar-picker-indicator]:-translate-y-1/2 [&::-moz-calendar-picker-indicator]:cursor-pointer"
                        />
                        {fieldErrors[field.id] && (
                            <p className="text-xs text-red-500 dark:text-red-400">
                                {fieldErrors[field.id]}
                            </p>
                        )}
                    </div>
                );

            case "select":
                return (
                    <Select
                        value={formData[field.id] || ""}
                        onValueChange={(value) =>
                            handleFieldChange(field.id, value)
                        }
                    >
                        <SelectTrigger>
                            <SelectValue
                                placeholder={
                                    field.placeholder ||
                                    `Select ${field.label.toLowerCase()}...`
                                }
                            />
                        </SelectTrigger>
                        <SelectContent>
                            {field.options?.map((option) => (
                                <SelectItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                );

            case "number":
                const hasError = fieldErrors[field.id];
                const minValue = field.validation?.min;
                const maxValue = field.validation?.max;
                
                return (
                    <div className="space-y-1">
                        <input
                            type="number"
                            id={field.id}
                            value={formData[field.id] || ""}
                            onChange={(e) => {
                                const value = e.target.value;
                                
                                // Prevent input that exceeds max or is below min
                                const numValue = parseFloat(value);
                                if (!isNaN(numValue)) {
                                    if (maxValue !== undefined && numValue > maxValue) {
                                        e.target.value = maxValue.toString();
                                        handleFieldChange(field.id, maxValue.toString());
                                        return;
                                    }
                                    if (minValue !== undefined && numValue < minValue) {
                                        e.target.value = minValue.toString();
                                        handleFieldChange(field.id, minValue.toString());
                                        return;
                                    }
                                }
                                
                                handleFieldChange(field.id, value);
                            }}
                            onKeyDown={(e) => {
                                // Prevent typing characters that would exceed limits
                                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                    const currentValue = parseFloat(e.target.value) || 0;
                                    const step = parseFloat(field.validation?.step) || 1;
                                    
                                    if (e.key === 'ArrowUp') {
                                        const newValue = currentValue + step;
                                        if (maxValue !== undefined && newValue > maxValue) {
                                            e.preventDefault();
                                        }
                                    } else if (e.key === 'ArrowDown') {
                                        const newValue = currentValue - step;
                                        if (minValue !== undefined && newValue < minValue) {
                                            e.preventDefault();
                                        }
                                    }
                                }
                            }}
                            placeholder={
                                field.placeholder ||
                                `Enter ${field.label.toLowerCase()}...`
                            }
                            required={effectiveRequired}
                            min={minValue}
                            max={maxValue}
                            step={field.validation?.step || "1"}
                            className={`flex h-10 w-full rounded-md border px-3 py-2 text-sm text-foreground ring-offset-background [color-scheme:light] file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:[color-scheme:dark] ${
                                hasError
                                    ? 'border-red-300 dark:border-red-700 bg-red-50/50 dark:bg-red-900/10 focus-visible:ring-red-500'
                                    : formSettings.calculateNumberFields 
                                        ? 'border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-900/10 focus-visible:ring-blue-500' 
                                        : 'border-input bg-background focus-visible:ring-ring'
                            }`}
                        />
                        {hasError && (
                            <div className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                                <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                                <span>{hasError}</span>
                            </div>
                        )}
                        {!hasError && formSettings.calculateNumberFields && (
                            <div className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
                                <span className="w-1 h-1 bg-blue-500 rounded-full"></span>
                                <span>Included in total calculation</span>
                            </div>
                        )}
                        {(minValue !== undefined || maxValue !== undefined) && !hasError && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                {minValue !== undefined && maxValue !== undefined 
                                    ? `Range: ${minValue} - ${maxValue}`
                                    : minValue !== undefined 
                                        ? `Minimum: ${minValue}`
                                        : `Maximum: ${maxValue}`
                                }
                            </div>
                        )}
                    </div>
                );

            case "email":
                return (
                    <input
                        type="email"
                        id={field.id}
                        value={formData[field.id] || ""}
                        onChange={(e) =>
                            handleFieldChange(field.id, e.target.value)
                        }
                        placeholder={
                            field.placeholder ||
                            `Enter ${field.label.toLowerCase()}...`
                        }
                        required={effectiveRequired}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                );

            case "checkbox":
                return (
                    <div className="flex items-center space-x-2">
                        <input
                            type="checkbox"
                            id={field.id}
                            checked={formData[field.id] || false}
                            onChange={(e) =>
                                handleFieldChange(field.id, e.target.checked)
                            }
                            required={effectiveRequired}
                            className="h-4 w-4 rounded border-gray-300 bg-background text-primary focus:ring-primary dark:border-gray-600 dark:[color-scheme:dark]"
                        />
                        <label
                            htmlFor={field.id}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                            {field.label}
                        </label>
                    </div>
                );

            case "radio":
                return (
                    <div className="space-y-2">
                        {field.options?.map((option) => (
                            <div
                                key={option.value}
                                className="flex items-center space-x-2"
                            >
                                <input
                                    type="radio"
                                    id={`${field.id}-${option.value}`}
                                    name={field.id}
                                    value={option.value}
                                    checked={
                                        formData[field.id] === option.value
                                    }
                                    onChange={(e) =>
                                        handleFieldChange(
                                            field.id,
                                            e.target.value
                                        )
                                    }
                                    required={effectiveRequired}
                                    className="h-4 w-4 border-gray-300 bg-background text-primary focus:ring-primary dark:border-gray-600 dark:[color-scheme:dark]"
                                />
                                <label
                                    htmlFor={`${field.id}-${option.value}`}
                                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                    {option.label}
                                </label>
                            </div>
                        ))}
                    </div>
                );

            default:
                return (
                    <input
                        type="text"
                        id={field.id}
                        value={formData[field.id] || ""}
                        onChange={(e) =>
                            handleFieldChange(field.id, e.target.value)
                        }
                        placeholder={
                            field.placeholder ||
                            `Enter ${field.label.toLowerCase()}...`
                        }
                        required={effectiveRequired}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                );
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-full min-h-[400px]">
                <div className="flex flex-col items-center gap-3">
                    <Loader2 className="h-8 w-8 animate-spin" />
                    <span className="text-lg">Loading form...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full overflow-hidden">
            {/* Scrollable content area */}
            <div className="flex-1 overflow-y-auto space-y-4 pr-2 pb-4">
                <form onSubmit={handleSubmit} className="space-y-4 px-1">
                    {/* {formName && (
                <div className="mb-4 p-3 bg-muted/10 rounded-lg">
                  <h4 className="text-sm font-medium text-muted-foreground">Form: {formName}</h4>
                </div>
              )}
               */}
                    {formFields.map((field) => {
                        if (!isFieldVisible(field)) return null;
                        const effectiveRequired = isFieldRequired(field);
                        return (
                            <div key={field.id} className="space-y-2">
                                {field.type !== "checkbox" && (
                                    <Label
                                        htmlFor={field.id}
                                        className="text-sm font-medium"
                                    >
                                        {field.label}
                                        {effectiveRequired && (
                                            <span className="text-red-500 ml-1">
                                                *
                                            </span>
                                        )}
                                    </Label>
                                )}
                                {renderField(field, effectiveRequired)}
                            </div>
                        );
                    })}

                    {/* Number Field Calculation Display */}
                    {formSettings.calculateNumberFields &&
                        formFields.some(
                            (field) =>
                                field.type === "number" && isFieldVisible(field),
                        ) && (
                        <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/40 rounded-full flex items-center justify-center flex-shrink-0">
                                        <span className="text-blue-600 dark:text-blue-400 text-sm font-bold">∑</span>
                                    </div>
                                    <div className="min-w-0">
                                        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                            {formSettings.totalCalculationName || "Total Calculation"}
                                        </h4>
                                        <p className="text-xs text-gray-600 dark:text-gray-400">
                                            Sum of all number fields
                                        </p>
                                    </div>
                                </div>
                                <div className="text-left sm:text-right">
                                    <div className="text-xl sm:text-2xl font-bold text-blue-600 dark:text-blue-400">
                                        {numberFieldTotal.toLocaleString()}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                        Max possible: {calculateMaxTotal().toLocaleString()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </form>
            </div>

            {/* Fixed footer - always at bottom */}
            <div className="flex-shrink-0 border-t bg-background pt-4 mt-4">
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:justify-between">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onCancel}
                        disabled={isSubmitting}
                        className="w-full order-2 sm:order-1"
                    >
                        {formSettings.resetButtonText || "Cancel"}
                    </Button>
                    <Button 
                        type="button"
                        onClick={handleSubmit}
                        disabled={
                            isSubmitting ||
                            formFields.some(
                                (f) =>
                                    isFieldVisible(f) &&
                                    fieldErrors[f.id] != null &&
                                    fieldErrors[f.id] !== "",
                            )
                        } 
                        className="w-full order-1 sm:order-2 dark:text-white"
                    >
                        {isSubmitting ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            // <CheckCircle className="mr-2 h-4 w-4" />
                            <></>
                        )}
                        {deferCaptureEnabled &&
                        deferDateFieldId &&
                        resolutionIndicatesDeferred(
                            formData,
                            formFields,
                            deferDateFieldId,
                        )
                            ? "Defer task"
                            : formSettings.submitButtonText || "Complete Task"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
